// Taşeron portalı — İş İzni detayı (api-contract §21/§22). S37-4 Carbon anatomisi: Breadcrumb →
// PageHeader (başlık + etiketler: görev türü [mavi], durum, tehlike; **salt-okunur not** =
// `helperText` — izin bilgilerini İSG uzmanı §14'te yönetir; supplier düzenlemez, yaşam döngüsü/
// Düzenle YOKTUR) → `DetailLayout` (<1440px yığılı, ≥1440px 336px meta + içerik). Meta (TAM İKİ
// Tile): **Bilgiler** (sahalar [çoklu — §14 Faz 2]/geçerlilik/oluşturulma) + **Gereksinimler**
// (iznin tipinde istenen belge türleri — §22, salt-okunur görünürlük, supplier burada belge
// YÜKLEMEZ; istenen KKD — §24.3, donanımı taşeron getirir, kontrol itemları §25 supplier'a GELMEZ;
// gerekli eğitimler). İçerik: `Tabs` Personel | Araçlar (Araçlar `hasVehicles` ya da bağlı araç varken; araç yoksa
// tek Tile, tek-öğeli sekme çubuğu YOK) — her sekme KENDİ `TableToolbar`ındaki eylemi taşır
// (Personel ata / Araç ekle). Tek `GET /supplier/permits/{uuid}` izin + istenen belge + gerekli
// eğitim + personel + readiness + araçları birlikte döndürür. Firma/contractor kimliği GÖNDERİLMEZ.

import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  CardContent,
  DetailLayout,
  HazardTag,
  PageHeader,
  Spinner,
  type TabItem,
  TabPanel,
  Tabs,
  Tag,
  type WorkPermitSiteDto,
  countNotReadyPersonnel,
  countNotReadyVehicles,
  currentCulture,
  hasVehicleSection,
  resolveApiErrorMessage,
  supplierPermitApi,
  useAuthStore,
  useDocumentTypes,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck, HardHat } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { PermitPersonnelPanel } from '../components/permits/PermitPersonnelPanel';
import { PermitVehiclesPanel } from '../components/permits/PermitVehiclesPanel';
import { permitStatusPresentation } from '../components/status/tone';
import { formatDate, formatDateTime } from '../lib/format';
import { supplierPermitKeys } from '../lib/supplierPermitKeys';

type DetailTab = 'personnel' | 'vehicles';

/** Etiket + değer satırı (bilgi bloğu). Boş değer tire ile gösterilir. */
function Fact({
  label,
  value,
  className,
}: {
  label: string;
  value: string | null | undefined;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-sm text-fg tnum">{value && value.length > 0 ? value : '—'}</dd>
    </div>
  );
}

/**
 * Sahalar bilgi bloğu (**çoklu lokasyon — §14 Faz 2**): iznin kapsadığı tüm sahaları etiket
 * listesi olarak gösterir; saha yoksa tire. Ada göre sıralı gelir (backend).
 */
function SitesFact({
  label,
  sites,
  className,
}: { label: string; sites: WorkPermitSiteDto[]; className?: string }) {
  return (
    <div className={className}>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 flex flex-wrap gap-1.5 text-sm text-fg">
        {sites.length === 0
          ? '—'
          : sites.map((s) => (
              <Tag key={s.uuid} color="gray">
                {s.name}
              </Tag>
            ))}
      </dd>
    </div>
  );
}

/** Gereksinimler Tile'ı içinde bir alt bölüm: başlık + (boşsa) boş metin ya da etiket listesi. */
function RequirementGroup({
  title,
  hint,
  emptyText,
  isEmpty,
  children,
}: {
  title: string;
  hint?: string;
  emptyText: string;
  isEmpty: boolean;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="text-xs text-muted">{title}</h3>
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
      {isEmpty ? <p className="text-sm text-muted">{emptyText}</p> : children}
    </section>
  );
}

export function PermitDetailPage() {
  const { t } = useTranslation();
  // İstenen belge etiketleri §27 kaydından (kayıtta yoksa ham kod); pasif tür karşılanamaz (GL #7).
  const { label: typeLabel, inactiveOrUnknown } = useDocumentTypes();
  const culture = currentCulture();
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);
  const { permitUuid = '' } = useParams();
  const [selectedTab, setSelectedTab] = useState<DetailTab>('personnel');
  const personnelAddRef = useRef<HTMLButtonElement | null>(null);

  const query = useQuery({
    queryKey: supplierPermitKeys.detail(companyId, permitUuid),
    queryFn: () => supplierPermitApi.getSupplierPermit(permitUuid),
    enabled: permitUuid.length > 0,
  });

  const detail = query.data;
  // İki sekme panelinin satırlarını taşıyan sorgu sonucunun damgaları (satır kalkınca odak dönüşü).
  const source = { dataUpdatedAt: query.dataUpdatedAt, errorUpdatedAt: query.errorUpdatedAt };
  const permit = detail?.permit;
  const status = permit ? permitStatusPresentation(permit.status) : null;

  // Araçlar sekmesi izin "araç olacak mı?" (`hasVehicles`) işaretliyken VEYA bağlı araç varken
  // (§14 "bayrak veya bağlı araç").
  const showVehicles = detail ? hasVehicleSection(detail.hasVehicles, detail.vehicles) : false;
  // Seçili sekme kaybolabilir (son araç izinden çıkarılınca bayraksız izinde Araçlar gider):
  // varsayılan sekmeye düş. Sekme çubuğu da kalktığından odak <body>'ye kaçtıysa "Personel ata"ya al.
  const activeTab: DetailTab = showVehicles ? selectedTab : 'personnel';
  useEffect(() => {
    if (selectedTab === activeTab) {
      return;
    }
    setSelectedTab(activeTab);
    const active = document.activeElement;
    if (!active || active === document.body) {
      personnelAddRef.current?.focus();
    }
  }, [selectedTab, activeTab]);
  // Rozet sayıları AYNI `detail` verisinden (satır etiketleriyle aynı sorgu → mutation sonrası
  // birlikte güncellenir). Yalnız sekme çubuğu varken (`showVehicles`) görünür; araç yoksa çubuk
  // render edilmez, mevcut "x/y hazır" özeti o durumu karşılar.
  const personnelIncomplete = detail ? countNotReadyPersonnel(detail.personnel) : 0;
  const vehiclesIncomplete = detail ? countNotReadyVehicles(detail.vehicles) : 0;
  const tabItems: TabItem[] = [
    {
      value: 'personnel',
      label: t('permits.detail.tab.personnel'),
      badge: {
        count: personnelIncomplete,
        srLabel: t('permits.detail.tab.personnelIncomplete', { count: personnelIncomplete }),
      },
    },
    {
      value: 'vehicles',
      label: t('permits.vehicles.section'),
      badge: {
        count: vehiclesIncomplete,
        srLabel: t('permits.detail.tab.vehiclesIncomplete', { count: vehiclesIncomplete }),
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb
        items={[
          { label: t('permits.title'), href: '/permits' },
          ...(permit ? [{ label: permit.title }] : []),
        ]}
      />

      {query.isPending ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Spinner label={t('permits.detail.loading')} />
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
            <ClipboardCheck className="h-10 w-10 text-danger" aria-hidden="true" />
            <p className="text-sm text-muted">{resolveApiErrorMessage(query.error, t)}</p>
            <Button asChild variant="secondary">
              <Link to="/permits">{t('permits.detail.back')}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : detail && permit && status ? (
        <>
          {/* Başlık + etiketler (salt-okunur — yaşam döngüsü/Düzenle YOK, eylem YOK). Sıra: görev
              türü, durum, tehlike (expert ile aynı). */}
          <PageHeader
            title={permit.title}
            tags={
              <>
                {permit.permitTypeName ? <Tag color="blue">{permit.permitTypeName}</Tag> : null}
                <Tag color={status.color}>{t(status.labelKey)}</Tag>
                <HazardTag hazardClass={permit.hazardClass} />
              </>
            }
            helperText={t('permits.detail.readOnlyNote')}
          />

          <DetailLayout
            meta={
              <>
                <Card>
                  <CardContent className="flex flex-col gap-4">
                    <h2 className="text-sm font-semibold text-fg">
                      {t('permits.detail.section.details')}
                    </h2>
                    <dl className="grid grid-cols-2 gap-4">
                      <SitesFact
                        className="col-span-2"
                        label={t('permits.detail.field.sites')}
                        sites={permit.sites}
                      />
                      <Fact
                        label={t('permits.detail.field.validityStart')}
                        value={formatDateTime(permit.validityStart, culture)}
                      />
                      <Fact
                        label={t('permits.detail.field.validityEnd')}
                        value={formatDateTime(permit.validityEnd, culture)}
                      />
                      <Fact
                        label={t('permits.detail.field.createdAt')}
                        value={permit.createdAt ? formatDate(permit.createdAt, culture) : null}
                      />
                    </dl>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="flex flex-col gap-4">
                    <h2 className="text-sm font-semibold text-fg">
                      {t('permits.detail.section.requirements')}
                    </h2>

                    <RequirementGroup
                      title={t('permits.detail.requiredDocuments')}
                      emptyText={t('permits.detail.requiredDocumentsEmpty')}
                      isEmpty={detail.requiredDocuments.length === 0}
                    >
                      <ul className="flex flex-wrap gap-1.5">
                        {detail.requiredDocuments.map((type) => (
                          <li
                            key={type}
                            className="flex min-w-0 max-w-full flex-wrap items-baseline gap-x-1.5 gap-y-0.5"
                          >
                            <Tag color="gray" wrap>
                              {typeLabel('personnel', type)}
                            </Tag>
                            {inactiveOrUnknown('personnel', type) ? (
                              <span className="shrink-0 text-xs text-muted">
                                {t('documentTypes.inactiveSuffix')}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                      {detail.requiredDocuments.some((type) =>
                        inactiveOrUnknown('personnel', type),
                      ) ? (
                        <p className="text-xs text-muted">{t('documentTypes.inactiveMissing')}</p>
                      ) : null}
                    </RequirementGroup>

                    {/* İstenen KKD (§24.3): salt-okunur bilgi, donanımı taşeron getirir. Tipe KKD
                        bağlı değilse boş durum. */}
                    <RequirementGroup
                      title={t('permits.detail.protectiveEquipment')}
                      hint={t('permits.detail.protectiveEquipmentHint')}
                      emptyText={t('permits.detail.protectiveEquipmentEmpty')}
                      isEmpty={detail.requiredProtectiveEquipment.length === 0}
                    >
                      <ul className="flex flex-wrap gap-1.5">
                        {detail.requiredProtectiveEquipment.map((item) => (
                          <li key={item.uuid} className="min-w-0 max-w-full">
                            <Tag color="gray" icon={<HardHat aria-hidden="true" />} wrap>
                              {item.name}
                            </Tag>
                          </li>
                        ))}
                      </ul>
                      {/* Bilgilendirici (engelleyici değil): listelenen KKD'ler ASGARİ'dir (§24.3). */}
                      <Alert tone="info">
                        {t('permits.detail.protectiveEquipmentMinimumNotice')}
                      </Alert>
                    </RequirementGroup>

                    <RequirementGroup
                      title={t('permits.detail.section.trainings')}
                      emptyText={t('permits.trainings.empty')}
                      isEmpty={detail.requiredTrainings.length === 0}
                    >
                      <ul className="flex flex-wrap gap-1.5">
                        {detail.requiredTrainings.map((training) => (
                          <li key={training.trainingUuid} className="min-w-0 max-w-full">
                            <Tag color="gray" wrap>
                              {training.title}
                            </Tag>
                          </li>
                        ))}
                      </ul>
                    </RequirementGroup>
                  </CardContent>
                </Card>
              </>
            }
            content={
              <div className="flex min-w-0 flex-col">
                {/* Tabs + Tile ARASINDA boşluk YOK (expert ile aynı); araç yoksa sekme çubuğu
                    HİÇ render edilmez (tek-öğeli Tabs yok). */}
                {showVehicles ? (
                  <Tabs
                    items={tabItems}
                    value={activeTab}
                    onValueChange={(value) => setSelectedTab(value as DetailTab)}
                    ariaLabel={t('permits.detail.tabs.label')}
                    idPrefix="permit-detail"
                  />
                ) : null}
                <Card className="carbon:p-0">
                  <CardContent className="carbon:p-0">
                    {showVehicles ? (
                      <>
                        {/* Her sekmenin paneli DOM'da kalır (`keepMounted`) — etkin olmayan
                            sekmenin `aria-controls` hedefi var olsun (APG). */}
                        <TabPanel
                          value="personnel"
                          activeValue={activeTab}
                          idPrefix="permit-detail"
                          keepMounted
                        >
                          <PermitPersonnelPanel
                            permitUuid={permit.uuid}
                            personnel={detail.personnel}
                            requiredDocuments={detail.requiredDocuments}
                            source={source}
                            addButtonRef={personnelAddRef}
                          />
                        </TabPanel>
                        <TabPanel
                          value="vehicles"
                          activeValue={activeTab}
                          idPrefix="permit-detail"
                          keepMounted
                        >
                          <PermitVehiclesPanel
                            permitUuid={permit.uuid}
                            vehicles={detail.vehicles}
                            requiredVehicleDocuments={detail.requiredVehicleDocuments}
                            source={source}
                          />
                        </TabPanel>
                      </>
                    ) : (
                      <PermitPersonnelPanel
                        permitUuid={permit.uuid}
                        personnel={detail.personnel}
                        requiredDocuments={detail.requiredDocuments}
                        source={source}
                        addButtonRef={personnelAddRef}
                      />
                    )}
                  </CardContent>
                </Card>
              </div>
            }
          />
        </>
      ) : null}
    </div>
  );
}
